import { useMemo, useState, type DragEvent } from "react";
import { IconBolt, IconChevronRight, IconSearch } from "@tabler/icons-react";
import { Input, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { Canal, CatalogoNotificaciones } from "../../services/notificaciones-service";
import { ETIQUETA_CANAL } from "../../utils/lienzo";
import { ICONO_CANAL } from "./iconos";

/** Tipo MIME del arrastre paleta → lienzo. */
export const MIME_ARRASTRE = "application/x-multiva-notificacion";

export type ElementoArrastrado = { tipo: "evento"; codigo: string } | { tipo: "canal"; canal: Canal };

function alArrastrar(e: DragEvent, elemento: ElementoArrastrado) {
  e.dataTransfer.setData(MIME_ARRASTRE, JSON.stringify(elemento));
  e.dataTransfer.effectAllowed = "move";
}

interface Props {
  catalogo: CatalogoNotificaciones;
  onAgregarEvento: (codigo: string) => void;
  onAgregarCanal: (canal: Canal) => void;
}

/** Paleta: se arrastra al lienzo (o se hace clic para agregar al final de su columna). */
export function PaletaLateral({ catalogo, onAgregarEvento, onAgregarCanal }: Props) {
  const [busqueda, setBusqueda] = useState("");
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());

  const grupos = useMemo(() => {
    const f = busqueda.trim().toLowerCase();
    const filtrados = catalogo.eventos.filter(
      (e) => !f || e.nombre.toLowerCase().includes(f) || e.codigo.toLowerCase().includes(f) || e.categoria.toLowerCase().includes(f),
    );
    const mapa = new Map<string, typeof filtrados>();
    for (const e of filtrados) mapa.set(e.categoria, [...(mapa.get(e.categoria) ?? []), e]);
    return [...mapa.entries()];
  }, [catalogo.eventos, busqueda]);

  const hayBusqueda = busqueda.trim().length > 0;

  return (
    <aside className="flex min-h-0 flex-col border-r border-border">
      <div className="space-y-2 border-b border-border p-3">
        <Text size="xs" fw={600} tt="uppercase" c="dimmed">Canales</Text>
        <div className="grid gap-1.5">
          {catalogo.canales.map((canal) => {
            const Icono = ICONO_CANAL[canal];
            return (
              <button
                key={canal}
                type="button"
                draggable
                onDragStart={(e) => alArrastrar(e, { tipo: "canal", canal })}
                onClick={() => onAgregarCanal(canal)}
                className="flex cursor-grab items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-left text-sm hover:border-multiva-600 active:cursor-grabbing"
                title="Arrastra al lienzo o haz clic para agregar"
              >
                <Icono size={16} className="text-multiva-600" />
                {ETIQUETA_CANAL[canal]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2 p-3 pb-2">
        <Text size="xs" fw={600} tt="uppercase" c="dimmed">Eventos · {catalogo.eventos.length}</Text>
        <Input placeholder="Buscar evento…" size="xs" leftSection={<IconSearch size={12} />} value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
        {grupos.length === 0 ? <Text size="xs" c="dimmed">Sin eventos que coincidan.</Text> : null}
        {grupos.map(([categoria, eventos]) => {
          const abierta = hayBusqueda || abiertas.has(categoria);
          return (
            <div key={categoria} className="mb-1">
              <button
                type="button"
                onClick={() =>
                  setAbiertas((s) => {
                    const n = new Set(s);
                    if (n.has(categoria)) n.delete(categoria);
                    else n.add(categoria);
                    return n;
                  })
                }
                className="flex w-full items-center gap-1 rounded px-1 py-1 text-left text-xs font-semibold hover:bg-muted"
              >
                <IconChevronRight size={12} className={cn("transition-transform", abierta && "rotate-90")} />
                <span className="flex-1">{categoria}</span>
                <span className="text-muted-foreground">{eventos.length}</span>
              </button>
              {abierta ? (
                <ul className="mt-0.5 space-y-0.5 pl-3">
                  {eventos.map((e) => (
                    <li key={e.codigo}>
                      <button
                        type="button"
                        draggable
                        onDragStart={(ev) => alArrastrar(ev, { tipo: "evento", codigo: e.codigo })}
                        onClick={() => onAgregarEvento(e.codigo)}
                        title={e.descripcion ?? e.codigo}
                        className="flex w-full cursor-grab items-start gap-1.5 rounded px-1.5 py-1 text-left text-xs hover:bg-muted active:cursor-grabbing"
                      >
                        <IconBolt size={12} className="mt-0.5 shrink-0 text-advertencia-600" />
                        <span>{e.nombre}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    </aside>
  );
}
